import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Divider,
    LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { isAuthenticated } from '../auth';

const API_URL = process.env.REACT_APP_API_URL;

// Map groupId to the eventId used by the presentations API
const GROUP_TO_EVENT = {
    '5e2a5e764175b97d84ec6cae': '6001d4e256a94550905723b1',
    '5e44050f4e6ed37ab467d0f2': '61e998f4f5b727e70e24bfef',
    '5e44052e4e6ed37ab467d0f3': '6001d42856a94550905723ae',
    '5e4405864e6ed37ab467d0f4': '61e9992cf5b727e70e24bff0',
    '5e4406454e6ed37ab467d0f5': '6001d4b056a94550905723b0',
    '5e5591a97ff73f6d34835a05': '63a331bc377a80f363a8073d',
    '639242d4971a30726884fd85': '640f65606164bc23c3f3eb4e',
};

// Map groupId to committee name
const GROUP_NAMES = {
    '5e2a5e764175b97d84ec6cae': 'Physical Committee',
    '5e44050f4e6ed37ab467d0f2': 'Electrical Committee',
    '5e44052e4e6ed37ab467d0f3': 'Technology Committee',
    '5e4405864e6ed37ab467d0f4': 'Civil Committee',
    '5e4406454e6ed37ab467d0f5': 'Business Strategy Committee',
    '5e5591a97ff73f6d34835a05': 'Leadership Committee',
    '639242d4971a30726884fd85': 'Innovation Committee',
};

const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const MonthlyCallsManager = ({ groupId }) => {
    const [presentations, setPresentations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [alert, setAlert] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState(''); // '', 'uploading', 'processing', 'done', 'error'

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [videoFile, setVideoFile] = useState(null);
    const [videoUrl, setVideoUrl] = useState('');

    const eventId = GROUP_TO_EVENT[groupId] || groupId;
    const committeeName = GROUP_NAMES[groupId] || '';

    useEffect(() => {
        loadPresentations();
    }, [groupId, selectedYear]);

    const loadPresentations = async (yearOverride) => {
        const yr = yearOverride || selectedYear;
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/presentationsbyGroup/${eventId}/${yr}`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();
            setPresentations(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setPresentations([]);
        }
        setLoading(false);
    };

    const showAlertMsg = (severity, message) => {
        setAlert({ severity, message });
        setTimeout(() => setAlert(null), 5000);
    };

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setMonth(new Date().getMonth() + 1);
        setYear(new Date().getFullYear());
        setVideoFile(null);
        setVideoUrl('');
        setEditingId(null);
        setShowForm(false);
        setUploadProgress(0);
        setUploadStatus('');
    };

    const handleEdit = (pres) => {
        setEditingId(pres._id);
        setTitle(pres.title || '');
        setDescription(pres.description || '');
        setVideoUrl(pres.video || '');
        setVideoFile(null);

        // Parse datePresented to month/year
        if (pres.datePresented) {
            const d = new Date(pres.datePresented);
            if (!isNaN(d)) {
                setMonth(d.getMonth() + 1);
                setYear(d.getFullYear());
            }
        }
        setShowForm(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!title.trim()) {
            showAlertMsg('error', 'Title is required');
            return;
        }

        setSaving(true);
        setUploadProgress(0);
        setUploadStatus(videoFile ? 'uploading' : 'processing');

        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('datePresented', `${year}-${String(month).padStart(2, '0')}-01`);
        formData.append('year', year.toString());
        formData.append('committeeName', committeeName);
        formData.append('eventId', eventId);
        formData.append('committee', groupId);

        if (videoFile) {
            formData.append('video', videoFile);
        } else if (videoUrl) {
            formData.append('video', videoUrl);
        }

        const url = editingId
            ? `${API_URL}/committee/${groupId}/recording/${editingId}`
            : `${API_URL}/committee/${groupId}/recording/create`;
        const method = editingId ? 'PUT' : 'POST';

        const xhr = new XMLHttpRequest();
        xhr.withCredentials = true;

        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
                const pct = Math.round((event.loaded / event.total) * 100);
                setUploadProgress(pct);
                if (pct >= 100) {
                    setUploadStatus('processing');
                }
            }
        });

        xhr.addEventListener('load', () => {
            setSaving(false);
            try {
                const data = JSON.parse(xhr.responseText);
                if (xhr.status >= 400 || data.error) {
                    setUploadStatus('error');
                    showAlertMsg('error', data.error || `Server error (${xhr.status})`);
                } else {
                    setUploadStatus('done');
                    showAlertMsg('success', editingId ? 'Recording updated successfully!' : 'Recording created successfully!');
                    // Switch table filter to the year we just saved so it shows up
                    const savedYear = data.year || year.toString();
                    setSelectedYear(savedYear);
                    resetForm();
                    loadPresentations(savedYear);
                }
            } catch {
                setUploadStatus('error');
                showAlertMsg('error', 'Unexpected server response');
            }
        });

        xhr.addEventListener('error', () => {
            setSaving(false);
            setUploadStatus('error');
            showAlertMsg('error', 'Network error — upload failed. Please check your connection and try again.');
        });

        xhr.addEventListener('abort', () => {
            setSaving(false);
            setUploadStatus('error');
            showAlertMsg('warning', 'Upload was cancelled.');
        });

        xhr.open(method, url);
        xhr.send(formData);
    };

    const handleDelete = async (id) => {
        try {
            const res = await fetch(`${API_URL}/committee/${groupId}/recording/${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await res.json();
            if (data.error) {
                showAlertMsg('error', data.error);
            } else {
                showAlertMsg('success', 'Recording deleted');
                setConfirmDelete(null);
                loadPresentations();
            }
        } catch (err) {
            showAlertMsg('error', 'Failed to delete');
            console.error(err);
        }
    };

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - i).toString());

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <VideoLibraryIcon sx={{ fontSize: 28, color: '#FF6600' }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#003E7E' }}>
                        Monthly Call Recordings
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={showForm ? <CloseIcon /> : <AddIcon />}
                    onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}
                    sx={{
                        backgroundColor: showForm ? '#666' : '#FF6600',
                        '&:hover': { backgroundColor: showForm ? '#555' : '#e55a00' },
                    }}
                >
                    {showForm ? 'Cancel' : 'Add Recording'}
                </Button>
            </Box>

            {alert && (
                <Alert severity={alert.severity} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            {/* Create/Edit Form */}
            {showForm && (
                <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 4, backgroundColor: 'rgba(0, 62, 126, 0.02)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#003E7E', mb: 3 }}>
                        {editingId ? 'Edit Recording' : 'New Recording'}
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            label="Title *"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            placeholder="e.g. March 2025 Monthly Call"
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="What was discussed..."
                            sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                            <FormControl sx={{ minWidth: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                                <InputLabel>Month</InputLabel>
                                <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                                    {months.map((m, i) => (
                                        <MenuItem key={i} value={i + 1}>{m}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: { xs: '100%', sm: 120 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                                <InputLabel>Year</InputLabel>
                                <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                                    {yearOptions.map(y => (
                                        <MenuItem key={y} value={parseInt(y)}>{y}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#003E7E', fontWeight: 600 }}>
                            Video Recording
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Upload a video file (up to 500MB) or paste a URL
                        </Typography>

                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{
                                mb: 1,
                                borderColor: '#003E7E',
                                color: '#003E7E',
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                py: 1.5,
                            }}
                        >
                            {videoFile ? videoFile.name : 'Choose video file...'}
                            <input
                                type="file"
                                hidden
                                accept="video/mp4,video/avi,video/mov,video/wmv,video/webm"
                                onChange={(e) => {
                                    if (e.target.files[0]) {
                                        setVideoFile(e.target.files[0]);
                                        setVideoUrl('');
                                    }
                                }}
                            />
                        </Button>

                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', my: 1 }}>
                            — or paste a video URL —
                        </Typography>

                        <TextField
                            label="Video URL"
                            value={videoUrl}
                            onChange={(e) => { setVideoUrl(e.target.value); setVideoFile(null); }}
                            fullWidth
                            placeholder="https://..."
                            disabled={!!videoFile}
                            sx={{ mb: 3 }}
                        />

                        {/* Upload Progress */}
                        {saving && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    mb: 2,
                                    borderRadius: 2,
                                    backgroundColor: uploadStatus === 'error' ? '#fef2f2' : '#f0f7ff',
                                    border: `1px solid ${uploadStatus === 'error' ? '#fecaca' : '#bfdbfe'}`,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                    {uploadStatus === 'uploading' && (
                                        <CircularProgress size={20} sx={{ color: '#FF6600' }} />
                                    )}
                                    {uploadStatus === 'processing' && (
                                        <CircularProgress size={20} sx={{ color: '#003E7E' }} />
                                    )}
                                    {uploadStatus === 'error' && (
                                        <CloseIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                                    )}
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: uploadStatus === 'error' ? '#d32f2f' : '#003E7E' }}>
                                        {uploadStatus === 'uploading' && `Uploading video... ${uploadProgress}%`}
                                        {uploadStatus === 'processing' && 'Upload complete — saving to server...'}
                                        {uploadStatus === 'error' && 'Upload failed'}
                                    </Typography>
                                </Box>
                                {(uploadStatus === 'uploading' || uploadStatus === 'processing') && (
                                    <LinearProgress
                                        variant={uploadStatus === 'processing' ? 'indeterminate' : 'determinate'}
                                        value={uploadProgress}
                                        sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            backgroundColor: '#e0e0e0',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: uploadStatus === 'processing' ? '#003E7E' : '#FF6600',
                                                borderRadius: 4,
                                            },
                                        }}
                                    />
                                )}
                                {uploadStatus === 'uploading' && videoFile && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(1)} MB)
                                    </Typography>
                                )}
                            </Paper>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <VideoLibraryIcon />}
                            sx={{
                                backgroundColor: '#003E7E',
                                py: 1.5,
                                fontWeight: 700,
                                '&:hover': { backgroundColor: '#002d5c' },
                            }}
                        >
                            {saving ? (uploadStatus === 'uploading' ? `Uploading ${uploadProgress}%...` : 'Saving...') : editingId ? 'Update Recording' : 'Create Recording'}
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Year filter */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Year</InputLabel>
                    <Select value={selectedYear} label="Year" onChange={(e) => setSelectedYear(e.target.value)}>
                        {yearOptions.map(y => (
                            <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Chip
                    label={`${presentations.length} recording${presentations.length !== 1 ? 's' : ''}`}
                    sx={{ backgroundColor: '#003E7E', color: 'white', fontWeight: 600 }}
                />
            </Box>

            {/* Recordings Table */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: '#003E7E' }} />
                </Box>
            ) : presentations.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                    <VideoLibraryIcon sx={{ fontSize: 48, color: '#003E7E', opacity: 0.3, mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                        No recordings for {selectedYear}. Click "Add Recording" to get started.
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 3, overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 500 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#003E7E' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Title</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Video</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {presentations.map((pres) => (
                                <TableRow key={pres._id} hover>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {pres.datePresented
                                                ? new Date(pres.datePresented).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                                                : 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {pres.title || 'Untitled'}
                                        </Typography>
                                        {pres.description && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {pres.description.length > 100
                                                    ? pres.description.substring(0, 100) + '...'
                                                    : pres.description}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {pres.video ? (
                                            <Tooltip title="Watch recording">
                                                <IconButton
                                                    href={`/presentation/${pres._id}`}
                                                    sx={{ color: '#FF6600' }}
                                                >
                                                    <PlayCircleOutlineIcon />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">No video</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => handleEdit(pres)} sx={{ color: '#003E7E' }}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" onClick={() => setConfirmDelete(pres._id)} sx={{ color: '#d32f2f' }}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Delete Confirmation */}
            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Delete Recording?</DialogTitle>
                <DialogContent>
                    <Typography>This will permanently remove this recording. This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
                    <Button onClick={() => handleDelete(confirmDelete)} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MonthlyCallsManager;
